// Iter 82.9 Sprint 2 — Payroll Run Lines panel.
// Per-employee payroll line table with quick filter + drill-down link.
// Row action: "Authorise Paid Days" (pay_pending/delta orchestration)
// when unpaid (LOP) days remain; enabled while the run is in an
// editable state (Draft / Calculated / Pending Approval) regardless of
// attendance freeze — disabled only once the run stage locks changes.
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MagnifyingGlass, Eye, Warning, HandCoins } from "@phosphor-icons/react";
import { api, formatApiError } from "../../../lib/api";
import { Input } from "../../../components/ui/input";
import { Badge } from "../../../components/ui/badge";
import { toast } from "sonner";
import AuthorisePaidDaysModal from "../../../components/payroll/AuthorisePaidDaysModal";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
const EDITABLE_STATES = ["Draft", "Calculated", "Pending Approval"];

export default function PayrollLinesPanel({ runId, calcVersion, runStatus, onRunChanged }) {
    const [lines, setLines] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [hasExcOnly, setHasExcOnly] = useState(false);
    const [authLine, setAuthLine] = useState(null);

    const editable = !runStatus || EDITABLE_STATES.includes(runStatus);

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (hasExcOnly) params.has_exception = true;
            const r = await api.get(`/payroll/run/${runId}/lines`, { params });
            setLines(r.data.items || []);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load lines"));
        } finally {
            setLoading(false);
        }
    }, [runId, hasExcOnly, calcVersion]);

    useEffect(() => { reload(); }, [reload]);

    const filtered = lines.filter((line) => {
        if (!search) return true;
        const emp = (line.snapshots || {}).employee || {};
        const q = search.toLowerCase();
        return (
            (emp.full_name || "").toLowerCase().includes(q)
            || (emp.employee_code || "").toLowerCase().includes(q)
            || (emp.department || "").toLowerCase().includes(q)
        );
    });

    return (
        <div className="space-y-3 pt-3" data-testid="lines-panel">
            <div className="flex items-center gap-2">
                <div className="relative flex-1 max-w-md">
                    <MagnifyingGlass size={14} className="absolute left-2.5 top-2.5 text-slate-400" />
                    <Input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search employee name / code / dept"
                        className="pl-8 h-9"
                        data-testid="lines-search"
                    />
                </div>
                <label className="flex items-center gap-1 text-xs text-slate-600">
                    <input
                        type="checkbox"
                        checked={hasExcOnly}
                        onChange={(e) => setHasExcOnly(e.target.checked)}
                        data-testid="lines-exc-toggle"
                    />
                    Has exceptions only
                </label>
            </div>
            <div className="border rounded-lg bg-white overflow-x-auto">
                <table className="w-full text-sm" data-testid="lines-table">
                    <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                        <tr>
                            <th className="px-3 py-2 text-left">Employee</th>
                            <th className="px-3 py-2 text-left">Dept</th>
                            <th className="px-3 py-2 text-right">Paid / LOP</th>
                            <th className="px-3 py-2 text-right">Gross</th>
                            <th className="px-3 py-2 text-right">Deductions</th>
                            <th className="px-3 py-2 text-right">Net Pay</th>
                            <th className="px-3 py-2 text-center">Flags</th>
                            <th className="px-3 py-2"></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {loading && <tr><td colSpan={8} className="px-3 py-6 text-center text-slate-400">Loading…</td></tr>}
                        {!loading && filtered.length === 0 && (
                            <tr><td colSpan={8} className="px-3 py-8 text-center text-slate-400" data-testid="lines-empty">No lines match.</td></tr>
                        )}
                        {filtered.map((line) => {
                            const emp = (line.snapshots || {}).employee || {};
                            const att = line.attendance_summary || {};
                            const isJoiner = !!line.joiner_doj;
                            const isLeaver = !!line.leaver_lwd;
                            const negNet = line.net_pay < 0;
                            const unpaidLeft = Number(att.lop_days || 0) > 0;
                            return (
                                <tr key={line.id} className="hover:bg-slate-50" data-testid={`line-row-${emp.employee_code || line.employee_id}`}>
                                    <td className="px-3 py-2">
                                        <div className="font-medium text-slate-800">{emp.full_name || line.employee_id}</div>
                                        <div className="text-[10px] font-mono text-slate-400">{emp.employee_code}</div>
                                    </td>
                                    <td className="px-3 py-2 text-xs">{emp.department || "—"}</td>
                                    <td className="px-3 py-2 text-right font-mono text-xs" data-testid={`line-paidlop-${emp.employee_code || line.employee_id}`}>{att.paid_days} / {att.lop_days}</td>
                                    <td className="px-3 py-2 text-right font-mono">{inr(line.gross_earnings)}</td>
                                    <td className="px-3 py-2 text-right font-mono text-rose-700">{inr(line.gross_deductions)}</td>
                                    <td className="px-3 py-2 text-right font-mono font-semibold">{inr(line.net_pay)}</td>
                                    <td className="px-3 py-2 text-center">
                                        <div className="flex items-center justify-center gap-1">
                                            {isJoiner && <Badge variant="outline" className="text-[10px]">Joiner</Badge>}
                                            {isLeaver && <Badge variant="outline" className="text-[10px]">Leaver</Badge>}
                                            {negNet && <Badge className="bg-rose-100 text-rose-800 text-[10px]"><Warning size={10} className="mr-0.5" />Neg Net</Badge>}
                                        </div>
                                    </td>
                                    <td className="px-3 py-2">
                                        <div className="flex flex-col gap-1">
                                            <Link
                                                to={`/admin/payroll/runs/${runId}/employee/${line.employee_id}`}
                                                className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:underline"
                                                data-testid={`line-detail-link-${emp.employee_code || line.employee_id}`}
                                            >
                                                <Eye size={12} /> Detail
                                            </Link>
                                            <Link
                                                to={`/admin/payroll/runs/${runId}/employee/${line.employee_id}/ops`}
                                                className="inline-flex items-center gap-1 text-xs text-emerald-700 hover:underline font-medium"
                                                data-testid={`line-ops-link-${emp.employee_code || line.employee_id}`}
                                            >
                                                <Eye size={12} /> Ops Console
                                            </Link>
                                            {unpaidLeft && (
                                                <button
                                                    type="button"
                                                    onClick={() => editable && setAuthLine(line)}
                                                    disabled={!editable}
                                                    title={editable ? "Authorise unpaid days as paid" : `Run is ${runStatus} — use the Correction Run workflow`}
                                                    className={`inline-flex items-center gap-1 text-xs font-medium text-left ${editable ? "text-amber-700 hover:underline" : "text-slate-400 cursor-not-allowed"}`}
                                                    data-testid={`line-authorise-btn-${emp.employee_code || line.employee_id}`}
                                                >
                                                    <HandCoins size={12} /> Authorise Paid Days
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            {authLine && (
                <AuthorisePaidDaysModal
                    open={!!authLine}
                    onClose={() => setAuthLine(null)}
                    runId={runId}
                    employeeId={authLine.employee_id}
                    employeeLabel={(authLine.snapshots || {}).employee?.full_name}
                    onCompleted={() => { reload(); onRunChanged?.(); }}
                />
            )}
        </div>
    );
}
